import { BaseQueryApi } from '@reduxjs/toolkit/query';

import getExtendedItems from '@/features/references/helpers/getExtendedItems';
import { IFiltersRawData } from '@/interfaces/filters.interfaces';
import { IGrpcService } from '@/services/GrpcService';
import injectContainer from '@/services/InjectContainer';
import ServiceTypes from '@/services/ServiceTypes';

import { FiltersParameters } from '../types';

import formatFiltersParameters from './formatFiltersParameters';
import makeFiltersAggregateItem from './makeFiltersAggregateItem';
import makeFiltersRawItem from './makeFiltersRawItem';

/**
 * Запрос на получение фильтров с доступными значениями и
 * преобразование данных к "сырому" формату Item
 * @param api
 * @param filtersData {object} Данные по всем фильтрам
 * @param parameters {object} Параметры запроса для получения всех фильтров
 * @param aggregateParameters {object} Параметры запроса для получения доступных фильтров
 */
const fetchAvailableFilters = async (
  api: BaseQueryApi,
  filtersData: IFiltersRawData,
  parameters: FiltersParameters,
  aggregateParameters: FiltersParameters,
) => {
  const grpcService = injectContainer.get<IGrpcService>(ServiceTypes.Grpc);
  const { itemsClient } = grpcService;

  const { filter, ...baseParameters } = aggregateParameters;

  const formattedAggregateParameters = formatFiltersParameters(aggregateParameters);

  // Запрос на получение доступных значений с учетом всех выбранных фильтров
  const { result: data = {} } = await itemsClient.aggregatePublished(
    formattedAggregateParameters,
  );

  const aggregateItem = makeFiltersAggregateItem(data, aggregateParameters);
  const [extendedAggregateItem] = await getExtendedItems(api, [aggregateItem]);

  const filtersRawItem = makeFiltersRawItem(extendedAggregateItem, aggregateParameters);

  const queries: string[] = [];
  const immutableQueries: string[] = [];

  (aggregateParameters.filter?.q || []).forEach(query => {
    const isImmutableQuery = (parameters.filter?.q || []).includes(query);
    if (isImmutableQuery) {
      immutableQueries.push(query);
    } else {
      queries.push(query);
    }
  });

  if (!queries.length) {
    return filtersRawItem;
  }

  const promises: Array<Promise<Record<string, any[]>>> = [];

  // Множество ключей, по котором идет фильтрация
  const filterKeys = Object.keys(filtersData);

  for (const filterKey of filterKeys) {
    const filterIndex =
      queries.findIndex(query => {
        // Query относится к данному полю
        const isFieldFound = aggregateParameters.filter?.qMap?.[filterKey] === query;
        return isFieldFound;
      }) ?? -1;

    // Если query с текущего фильтра нет, то фильтр не был выбран
    if (filterIndex < 0) {
      continue;
    }

    const queriesWithoutSomeQuery =
      queries?.filter((_, index) => index !== filterIndex) || [];

    // Если фильтрация происходит по одному фильтру,
    // то выполнять запрос на получение доступных значений нет необходимости,
    // доступные значения можно получить из данных по фильтрам без фильтрации.
    if (!queriesWithoutSomeQuery.length) {
      const promise = Promise.resolve({
        [filterKey]: filtersData?.[filterKey],
      });
      promises.push(promise);
    } else {
      // Формирование параметров запроса без фильтрации по текущей коллекции
      const parameters = formatFiltersParameters({
        ...baseParameters,
        filter: {
          ...filter,
          q: [...immutableQueries, ...queriesWithoutSomeQuery],
        },
      });

      const formattedParameters = formatFiltersParameters(parameters);

      // Получение доступных значений для коллекции в фильтре
      const promise = itemsClient
        .aggregatePublished(formattedParameters)
        .then(({ result = {} }) => {
          const aggregateItem = makeFiltersAggregateItem(result, parameters);

          return getExtendedItems(api, [aggregateItem]);
        })
        .then(([extendedAggregateItem]) => {
          const { data } = makeFiltersRawItem(extendedAggregateItem, parameters);
          return {
            // Возможные значения фильтра, которые был исключены из фильтрации
            [filterKey]: data?.[filterKey],
          };
        });

      promises.push(promise);
    }
  }

  const results = await Promise.all(promises);

  // Слияние доступных значений фильтров с полной фильтрацией и
  // доступных значений по коллекциям выбранных в фильтре
  Object.assign(filtersRawItem.data, ...results);

  return filtersRawItem;
};

export default fetchAvailableFilters;
